import { useAppDispatch, useAppSelector } from "@store/store-helper";
import { toggleItemSelection } from "@utils/array-utils";
import { selectedRowIdsSelector } from "@store/table/table-selector";
import { setInitialFetchingItems } from "@store/table/table-slice";
import { DeleteIcon, EmailIcon, InfoIcon, ResendEmailIcon, SeatIcon, SelectionIcon, TeamIcon, UserRoleIcon } from "@flat-ui";
import { useMembersUtils } from "@hooks/use-member-utils";
import { useMediaQueryList } from "@hooks/use-media-query";
import { MemberRemoveMenuButton } from "@components/table/members/member-remove-menu-button";
import { MemberRoleMenuButton } from "@components/table/members/member-role-menu-button";
import { TeamRemoveMenuButton } from "@components/table/members/team-remove-menu-button";
import { useProjectRemoveMenuOption } from "@hooks/use-project-remove-menu-option";
import { useCompanyId } from "@context-providers/company-id-provider";
import { getMailHref } from "@utils/email-utils";
import { useHasUserValidRoleCompanyLevel } from "@hooks/access-control/use-has-user-valid-role-company-level";
import { useHasUserValidFeatureCompanyLevel } from "@hooks/feature-control/use-has-user-valid-feature-company-level";
import { isGroupMember, isProjectMemberBase } from "@custom-types/member-type-guards";
import { useHasUserValidRoleGroupLevel } from "@hooks/access-control/use-has-user-valid-role-group-level";
import { useProjectMemberActions } from "@hooks/projects/use-project-member-actions";
import { useTeamsAccess } from "@hooks/access-control/use-teams-access";
import { getCompanyByIdSelector } from "@store/sdb-company/sdb-company-selector";
import { currentUserSelector } from "@store/user/user-selector";
import { useResendInvitation } from "@hooks/use-resend-invitation";
import { ManageSeatsMenuButton } from "@components/table/members/assign-seats/manage-seats-menu-button";
import { MANAGE_SEATS_TITLE } from "@components/table/members/assign-seats/manage-seats-dialog";
import { useGetMemberSeats } from "@components/table/members/assign-seats/use-member-seats";
import { shouldOfferToManageSeats } from "@components/table/members/assign-seats/assign-seats-utils";
import { AddToTeamMenuButton } from "@components/table/members/add-to-team-menu-button";
import { ADD_TO_TEAM_TITLE } from "@components/teams/add-to-team/add-to-team-constants";
/**
 * Returns menu options for the members list view (both desktop 3-dot menu and mobile).
 * Provides actions for selecting a member, viewing profile, changing role,
 * sending email, resending invitation, and removing member.
 */
export function useMembersMenuOptions({
  subjectType,
  selectedProject,
  group,
  team
}) {
  const dispatch = useAppDispatch();
  const companyId = useCompanyId();
  const {
    openProfilePage
  } = useMembersUtils();
  const selectedRowIds = useAppSelector(selectedRowIdsSelector);
  const {
    isScreenSmOrSmaller
  } = useMediaQueryList();
  const selectedSdbCompany = useAppSelector(getCompanyByIdSelector(companyId));
  const {
    canDeleteMemberFromGroup
  } = useHasUserValidRoleGroupLevel({
    selectedGroup: group ?? null
  });
  const {
    canDeleteMemberFromCompany,
    canEditMember,
    canResendInvitationToCompany
  } = useHasUserValidRoleCompanyLevel();
  const {
    canDeleteMembersFromTeam,
    canInviteMembersToTeam
  } = useTeamsAccess();
  const {
    canAssignManagers
  } = useHasUserValidFeatureCompanyLevel();
  const currentUser = useAppSelector(currentUserSelector);
  const projectMemberActions = useProjectMemberActions(selectedProject);
  const getMemberSeats = useGetMemberSeats();
  const {
    projectRemoveMenuOption,
    shouldShowProjectRemove
  } = useProjectRemoveMenuOption({
    companyId,
    selectedProject
  });
  const {
    resendInvitation,
    shouldShowResendInvitation
  } = useResendInvitation({
    companyId,
    subjectType,
    selectedProject,
    canResendInvitationToCompany
  });

  // An auto-managed team syncs its membership externally, so nobody may be removed from it.
  const canRemoveFromTeam = canDeleteMembersFromTeam && !!team && !team.category.isManaged;

  /** Whether the remove action should be shown for the given member */
  function shouldShowRemove(member) {
    if (subjectType === "team" && team) {
      return canRemoveFromTeam || canDeleteMemberFromCompany;
    }
    if (subjectType === "project") {
      return shouldShowProjectRemove(member);
    }
    if (group && subjectType === "group" && isGroupMember(member) && canDeleteMemberFromGroup) {
      return true;
    }
    if (subjectType === "workspace" && selectedSdbCompany && !isProjectMemberBase(member) && canDeleteMemberFromCompany) {
      return true;
    }
    return false;
  }

  /**
   * Whether the user could change any of the seats of the member, see shouldOfferToManageSeats.
   *
   * The seats belong to the workspace subscription, so only the workspace members table knows them.
   */
  function shouldShowManageSeats(member_0) {
    return subjectType === "workspace" && shouldOfferToManageSeats(getMemberSeats(member_0).seatOptions);
  }
  return buildMenuOptions();

  /** Builds the full list of menu options */
  function buildMenuOptions() {
    return [{
      label: "Select member",
      Icon: SelectionIcon,
      onSelect: member_1 => {
        const newSelectedRowIds = toggleItemSelection(selectedRowIds, member_1.identity);
        dispatch(setInitialFetchingItems(newSelectedRowIds));
      },
      shouldShow: () => isScreenSmOrSmaller,
      shouldShowDividerAfter: isScreenSmOrSmaller
    }, {
      label: "Profile",
      Icon: InfoIcon,
      onSelect: member_2 => {
        openProfilePage({
          member: member_2,
          source: "projects-menu-option"
        });
      },
      shouldShowDividerAfter: true
    }, ...getRoleMenuOption(), {
      label: MANAGE_SEATS_TITLE,
      Icon: SeatIcon,
      component: (member_3, onMenuClose) => <ManageSeatsMenuButton member={member_3} onMenuClose={onMenuClose} />,
      shouldShow: shouldShowManageSeats
    }, {
      label: ADD_TO_TEAM_TITLE,
      Icon: TeamIcon,
      component: (member_4, onMenuClose_0) => <AddToTeamMenuButton member={member_4} onMenuClose={onMenuClose_0} />,
      // Only offered in the workspace members table: the team page has its own invite dialog
      shouldShow: () => subjectType === "workspace" && canInviteMembersToTeam
    }, {
      label: "Send email",
      Icon: EmailIcon,
      onSelect: member_5 => {
        window.location.href = getMailHref({
          members: [member_5]
        });
      },
      shouldShowDividerAfter: member_6 => !shouldShowResendInvitation(member_6) && shouldShowRemove(member_6)
    }, {
      label: "Resend invitation",
      Icon: ResendEmailIcon,
      onSelect: async member_7 => {
        await resendInvitation(member_7);
      },
      shouldShow: shouldShowResendInvitation,
      shouldShowDividerAfter: member_8 => shouldShowRemove(member_8)
    }, ...getRemoveMenuOptions()];
  }

  /** Whether the role of the given member can be changed from the menu */
  function shouldShowRoleChange(member_9) {
    if (subjectType === "project") {
      return projectMemberActions.canChangeRole(member_9);
    }

    // Without manager roles (Scan Essential and below) the workspace has a single
    // admin (the main owner) whose role can't be changed, so hide it there.
    return canAssignManagers || subjectType !== "workspace" || member_9.identity !== currentUser?.identity;
  }

  /** Returns the role change menu option if the user has permission */
  function getRoleMenuOption() {
    // Project roles are checked per member, the same as in the role column and the share dialog
    if (subjectType !== "project" && !canEditMember) {
      return [];
    }
    const shouldSupportRoleChange = subjectType === "workspace" || subjectType === "project" || subjectType === "group" && canAssignManagers || subjectType === "team";
    if (!shouldSupportRoleChange) {
      return [];
    }
    return [{
      label: "Change role",
      Icon: UserRoleIcon,
      shouldShow: member_10 => shouldShowRoleChange(member_10),
      component: (member_11, onMenuClose_1) => <MemberRoleMenuButton member={member_11} companyId={companyId} subjectType={subjectType} selectedProject={selectedProject} group={group} onMenuClose={onMenuClose_1} />
    }];
  }

  /** Returns the remove menu option(s) based on the subject type */
  function getRemoveMenuOptions() {
    if (subjectType === "team" && team) {
      return [{
        label: "Remove member",
        Icon: DeleteIcon,
        component: (member_12, onMenuClose_2) => <TeamRemoveMenuButton companyId={companyId} team={team} member={member_12} canRemoveFromTeam={canRemoveFromTeam} canRemoveFromWorkspace={canDeleteMemberFromCompany} onMenuClose={onMenuClose_2} />,
        shouldShow: () => canRemoveFromTeam || canDeleteMemberFromCompany
      }];
    }
    if (subjectType === "project") {
      return projectRemoveMenuOption;
    }
    return [{
      label: "Remove member",
      Icon: DeleteIcon,
      component: (member_13, onMenuClose_3) => <MemberRemoveMenuButton member={member_13} companyId={companyId} subjectType={subjectType} selectedProject={selectedProject} group={group} onMenuClose={onMenuClose_3} />,
      shouldShow: shouldShowRemove
    }];
  }
}